import * as React from "react";
import { X } from "lucide-react";
import { cn } from "../../lib/cn";

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
  /** Max width class, defaults to max-w-md */
  size?: "sm" | "md" | "lg" | "xl";
}

const sizeClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
};

export function Dialog({ isOpen, onClose, title, description, children, className, size = "md" }: DialogProps) {
  // Close on Escape
  React.useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} aria-hidden="true" />
      {/* Panel */}
      <div role="dialog" aria-modal="true" aria-labelledby={title ? "dialog-title" : undefined}
        className={cn(
          "relative w-full bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden",
          sizeClasses[size], className,
        )}>
        {/* Header */}
        {(title || description) && (
          <div className="px-5 pt-5 pb-4 border-b border-gray-100">
            {title && <h2 id="dialog-title" className="text-base font-semibold text-gray-900">{title}</h2>}
            {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
          </div>
        )}
        {/* Close button */}
        <button onClick={onClose} aria-label="Close dialog"
          className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
          <X className="w-4 h-4" />
        </button>
        {/* Body */}
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

export function DialogFooter({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("flex items-center justify-end gap-2 pt-4 border-t border-gray-100 mt-4", className)}>
      {children}
    </div>
  );
}
